'use client';

import { useEffect, useRef, useState } from "react";
import { Monitor, X } from "lucide-react";

/**
 * Displays a mobile-only desktop recommendation banner with dismissal memory.
 */
const STORAGE_KEY = "holescale_desktop_notice_dismissed";
const MOBILE_BREAKPOINT = 768;
const DISMISS_DURATION_MS = 7 * 24 * 60 * 60 * 1000; // 7 days
const SHOW_DELAY_MS = 1500; // 1.5 seconds

export default function MobileDesktopNotice() {
  const [isVisible, setIsVisible] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const showTimerRef = useRef<number | null>(null);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    const clearTimer = () => {
      if (showTimerRef.current) {
        window.clearTimeout(showTimerRef.current);
        showTimerRef.current = null;
      }
    };

    const hasRecentDismissal = () => {
      try {
        const storedValue = localStorage.getItem(STORAGE_KEY);
        if (!storedValue) {
          return false;
        }
        const dismissedAt = parseInt(storedValue, 10);
        if (Number.isNaN(dismissedAt)) {
          return false;
        }
        return Date.now() - dismissedAt < DISMISS_DURATION_MS;
      } catch {
        return false;
      }
    };

    const shouldShowNotice = () => window.innerWidth < MOBILE_BREAKPOINT && !hasRecentDismissal();

    const scheduleShow = () => {
      if (showTimerRef.current) return;
      showTimerRef.current = window.setTimeout(() => {
        setIsClosing(false);
        setIsVisible(true);
        showTimerRef.current = null;
      }, SHOW_DELAY_MS);
    };

    const handleVisibilityEvaluation = () => {
      if (shouldShowNotice()) {
        scheduleShow();
      } else {
        clearTimer();
        setIsClosing(false);
        setIsVisible(false);
      }
    };

    handleVisibilityEvaluation();
    window.addEventListener("resize", handleVisibilityEvaluation);
    window.addEventListener("orientationchange", handleVisibilityEvaluation);

    return () => {
      clearTimer();
      window.removeEventListener("resize", handleVisibilityEvaluation);
      window.removeEventListener("orientationchange", handleVisibilityEvaluation);
    };
  }, []);

  const handleDismiss = () => {
    setIsClosing(true);
    try {
      localStorage.setItem(STORAGE_KEY, Date.now().toString());
    } catch {
      // Ignore write errors (e.g., private mode)
    }

    window.setTimeout(() => {
      setIsVisible(false);
      setIsClosing(false);
    }, 300);
  };

  if (!isVisible) {
    return null;
  }

  return (
    <div
      className={`
        fixed inset-x-4 bottom-4 z-50 transition-transform duration-300
        ease-out md:hidden
        ${isClosing ? "translate-y-full opacity-0" : "translate-y-0 opacity-100"}
      `}
    >
      <div className="flex items-center gap-2 rounded-lg bg-popover/90 px-4 py-2.5 text-sm text-white shadow-lg backdrop-blur">
        <Monitor className="size-4 shrink-0" />
        <span>Works best on desktop</span>
        <button
          type="button"
          onClick={handleDismiss}
          aria-label="Dismiss message"
          className="-mr-2 ml-auto flex min-h-[44px] min-w-[44px] items-center justify-center rounded-lg transition-colors hover:bg-muted/50"
        >
          <X className="size-4" />
        </button>
      </div>
    </div>
  );
}

